// AdminUploadCurrentStudents.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  DialogActions,
  Dialog as MuiDialog,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Grid,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ListIcon from '@mui/icons-material/List';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import api from '../../../API/axiosInstance';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminUploadCurrentStudents = ({ open, onClose }) => {
  const [currentTab, setCurrentTab] = useState(0);
  
  // Upload tab states
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [importedRecords, setImportedRecords] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Uploaded Details tab states
  const gridRef = useRef(null);
  const [uploadedStudents, setUploadedStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const resetAll = () => {
    setFile(null);
    setParsing(false);
    setSheetName('');
    setHeaders([]);
    setRows([]);
    setIsDownloading(false);
    setIsUploading(false);
    setConfirmOpen(false);
    setUploadStatus(null);
    setResponseMessage('');
    setImportedRecords(0);
    setErrorMessage('');
  };

  const handleCloseDialog = () => {
    resetAll();
    setCurrentTab(0);
    setUploadedStudents([]);
    setStats(null);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      // Load uploaded students when switching to details tab
      fetchUploadedStudents();
      fetchStats();
    }
  };

  const fetchUploadedStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await api.get('/StudentRequest/get-all-uploaded-students?isActive=true');
      console.log('API Response:', response.data);
      console.log('Response length:', response.data?.length);
      if (response.data && response.data.length > 0) {
        console.log('First item structure:', response.data[0]);
        console.log('First item keys:', Object.keys(response.data[0]));
      }
      
      // Convert PascalCase to camelCase for grid compatibility
      const convertedData = (response.data || []).map((item, index) => {
        const converted = {
        uploadId: item.uploadId || item.UploadId,
        erpStudentId: item.erpStudentId || item.ErpStudentId,
        rollNum: item.rollNum || item.RollNum,
        regNum: item.regNum || item.RegNum,
        firstName: item.firstName || item.FirstName,
        lastName: item.lastName || item.LastName,
        fullName: item.fullName || item.FullName,
        email: item.email || item.Email,
        mobile: item.mobile || item.Mobile,
        whatsAppNumber: item.whatsAppNumber || item.WhatsAppNumber,
        instTypeId: item.instTypeId || item.InstTypeId,
        instId: item.instId || item.InstId,
        categoryId: item.categoryId || item.CategoryId,
        degreeId: item.degreeId || item.DegreeId,
        deptId: item.deptId || item.DeptId,
        batchYear: item.batchYear || item.BatchYear,
        courseId: item.courseId || item.CourseId,
        invitationSent: item.invitationSent ?? item.InvitationSent,
        invitationSentDate: item.invitationSentDate || item.InvitationSentDate,
        invitationSentBy: item.invitationSentBy || item.InvitationSentBy,
        uploadedBy: item.uploadedBy || item.UploadedBy,
        uploadedDate: item.uploadedDate || item.UploadedDate,
        isActive: item.isActive ?? item.IsActive,
        portalRequestId: item.portalRequestId || item.PortalRequestId,
        portalRequestStatus: item.portalRequestStatus || item.PortalRequestStatus,
        portalRequestedDate: item.portalRequestedDate || item.PortalRequestedDate,
        portalApprovedDate: item.portalApprovedDate || item.PortalApprovedDate,
        portalRejectedDate: item.portalRejectedDate || item.PortalRejectedDate,
        portalRejectionReason: item.portalRejectionReason || item.PortalRejectionReason,
        portalConnected: item.portalConnected ?? item.PortalConnected,
        portalConnectedDate: item.portalConnectedDate || item.PortalConnectedDate,
        uploadedByName: item.uploadedByName || item.UploadedByName,
        invitationSentByName: item.invitationSentByName || item.InvitationSentByName,
        // Master data names
        instype: item.instype || item.INSTYPE,
        intname: item.intname || item.INTNAME,
        cat: item.cat || item.CAT,
        dept: item.dept || item.DEPT,
        deg: item.deg || item.DEG,
        coursename: item.coursename || item.COURSENAME,
        };
        
        if (index === 0) {
          console.log('First converted item:', converted);
          console.log('First converted item keys:', Object.keys(converted));
        }
        
        return converted;
      });
      
      console.log('Converted data:', convertedData);
      if (convertedData.length > 0) {
        console.log('Sample row data for grid:', {
          erpStudentId: convertedData[0].erpStudentId,
          fullName: convertedData[0].fullName,
          rollNum: convertedData[0].rollNum,
          email: convertedData[0].email,
        });
      }
      setUploadedStudents(convertedData);
    } catch (error) {
      console.error('Error fetching uploaded students:', error);
      setErrorMessage('Failed to load uploaded students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/StudentRequest/upload-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const statusCellRenderer = useCallback((params) => {
    const data = params.data;
    if (data.portalRequestStatus) {
      const status = data.portalRequestStatus.toLowerCase();
      const color = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
      return (
        <Chip 
          label={data.portalRequestStatus} 
          color={color} 
          size="small" 
          sx={{ fontSize: '11px', height: '24px' }}
        />
      );
    }
    return (
      <Chip 
        label="No Request" 
        color="default" 
        size="small" 
        sx={{ fontSize: '11px', height: '24px' }}
      />
    );
  }, []);

  const invitationStatusCellRenderer = useCallback((params) => {
    const sent = params.data.invitationSent;
    return (
      <Chip 
        label={sent ? 'Sent' : 'Pending'} 
        color={sent ? 'success' : 'warning'} 
        size="small" 
        sx={{ fontSize: '11px', height: '24px' }}
      />
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
    },
    { field: "erpStudentId", headerName: "ERP ID", width: 90, filter: true },
    { field: "fullName", headerName: "Name", width: 160, filter: true },
    { field: "rollNum", headerName: "Roll #", width: 90, filter: true },
    { field: "regNum", headerName: "Reg #", width: 90, filter: true },
    { field: "email", headerName: "Email", width: 180, filter: true },
    { field: "mobile", headerName: "Mobile", width: 110, filter: true },
    { field: "batchYear", headerName: "Batch", width: 80, filter: true },
    { field: "instype", headerName: "Inst. Type", width: 120, filter: true },
    { field: "intname", headerName: "Institute", width: 140, filter: true },
    { field: "cat", headerName: "Category", width: 120, filter: true },
    { field: "dept", headerName: "Department", width: 140, filter: true },
    { field: "deg", headerName: "Degree", width: 120, filter: true },
    { field: "coursename", headerName: "Course", width: 140, filter: true },
    {
      field: "invitationSent",
      headerName: "Invitation",
      width: 110,
      cellRenderer: invitationStatusCellRenderer,
      filter: true,
      sortable: true,
    },
    {
      field: "portalRequestStatus",
      headerName: "Portal Status",
      width: 130,
      cellRenderer: statusCellRenderer,
      filter: true,
      sortable: true,
    },
    {
      field: "portalConnected",
      headerName: "Connected",
      width: 100,
      cellRenderer: (params) => (
        params.data.portalConnected ? (
          <Chip 
            label="Yes" 
            color="success" 
            size="small" 
            sx={{ fontSize: '11px', height: '24px' }}
          />
        ) : (
          <Chip 
            label="No" 
            color="default" 
            size="small" 
            sx={{ fontSize: '11px', height: '24px' }}
          />
        )
      ),
      filter: true,
      sortable: true,
    },
    { 
      field: "uploadedDate", 
      headerName: "Uploaded Date", 
      width: 130,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
      filter: true,
      sortable: true,
    },
  ], [statusCellRenderer, invitationStatusCellRenderer]);

  const humanSize = (nBytes) => {
    if (!nBytes && nBytes !== 0) return '';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = nBytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i += 1;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    await parseExcel(selected);
  };

  const parseExcel = async (selectedFile) => {
    setParsing(true);
    setHeaders([]);
    setRows([]);
    setErrorMessage('');
    try {
      const XLSX = await import('xlsx');
      const data = await selectedFile.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });

      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const hdrs = json.length ? Object.keys(json[0]) : [];

      setSheetName(wsName);
      setHeaders(hdrs);
      setRows(json);
    } catch (err) {
      console.error('Excel parse error:', err);
      setErrorMessage('Failed to parse the Excel file. Please ensure it is a valid .xlsx or .xls file.');
    } finally {
      setParsing(false);
    }
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('/StudentRequest/download-template', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CurrentStudentsTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download the template. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAskConfirm = () => {
    if (!file || rows.length === 0) {
      setErrorMessage('Please upload a valid Excel file with at least one data row before importing.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setConfirmOpen(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/StudentRequest/upload-current-students?uploadedByUserId=1`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const result = response.data;
      setResponseMessage(result.message || 'Imported successfully.');
      setImportedRecords(result.insertedCount || rows.length);
      setUploadStatus('success');
      
      // Refresh uploaded students list if on details tab
      if (currentTab === 1) {
        fetchUploadedStudents();
        fetchStats();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to upload the file. Please try again.');
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullScreen>
      {isUploading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3 }} />}

      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={handleCloseDialog} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogTitle sx={{ pr: 8, fontWeight: 'bold', color: 'primary.main' }}>
        Upload Current Students
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 2 }}>
        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
            <Tab 
              icon={<CloudUploadIcon />} 
              iconPosition="start"
              label="Upload" 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<ListIcon />} 
              iconPosition="start"
              label="Uploaded Details" 
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        <MDBox display="flex" flexDirection="column" gap={2}>
          {/* Upload Tab Content */}
          {currentTab === 0 && (
            <>
          {/* SUCCESS */}
          {uploadStatus === 'success' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Upload Successful
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {responseMessage || 'Uploaded successfully.'}
                </MDTypography>
                <Chip label={`Records imported: ${importedRecords}`} color="success" variant="outlined" sx={{ mb: 2 }} />
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Upload Another File
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ERROR */}
          {uploadStatus === 'error' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ErrorIcon color="error" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Upload Failed
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {errorMessage || 'There was an error uploading the file.'}
                </MDTypography>
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Try Again
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* MAIN CONTENT */}
          {!uploadStatus && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Current Students Data Upload
                </MDTypography>

                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  disabled={isDownloading}
                >
                  {isDownloading ? 'Downloading…' : 'Download Template'}
                </MDButton>
              </Box>

              <Alert icon={<InfoOutlinedIcon />} severity="info">
                Download the template, fill in the current student details, upload the Excel file, review the summary, and then confirm to import.
              </Alert>

              {/* Upload Card */}
              <Card
                variant="outlined"
                sx={{
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: file ? 'success.main' : 'divider',
                  bgcolor: '#fff',
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="excel-file-input-current-students"
                  />
                  <UploadFileIcon sx={{ fontSize: 54, mb: 1, color: 'primary.main' }} />
                  <MDTypography variant="h6" gutterBottom>
                    {file ? file.name : 'Upload Excel File'}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {file
                      ? `${humanSize(file.size)} • Last modified: ${new Date(file.lastModified).toLocaleDateString()}`
                      : 'Drag & drop an Excel file here, or click to browse'}
                  </MDTypography>
                  <label htmlFor="excel-file-input-current-students">
                    <MDButton
                      variant="contained"
                      component="span"
                      color="info"
                      startIcon={<UploadFileIcon />}
                      disabled={parsing}
                    >
                      {file ? 'Change File' : 'Select File'}
                    </MDButton>
                  </label>
                  {parsing && (
                    <Box mt={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={18} />
                      <MDTypography variant="caption">Parsing Excel…</MDTypography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Summary */}
              {!!rows.length && (
                <Card variant="outlined">
                  <CardContent>
                    <MDTypography variant="subtitle1" sx={{ mb: 1 }}>
                      Summary
                    </MDTypography>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                      <Chip label={`Sheet: ${sheetName || 'CurrentStudentsTemplate'}`} variant="outlined" />
                      <Chip label={`Columns: ${headers.length}`} variant="outlined" />
                      <Chip label={`Detected rows: ${rows.length}`} color="primary" />
                    </Box>
                  </CardContent>
                </Card>
              )}

              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}

              <Box display="flex" gap={2} mt="auto" justifyContent="flex-end">
                <MDButton variant="outlined" color="secondary" onClick={handleCloseDialog}>
                  Cancel
                </MDButton>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleAskConfirm}
                  disabled={!file || parsing || rows.length === 0 || isUploading}
                  startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isUploading ? 'Uploading…' : 'Confirm & Upload'}
                </MDButton>
              </Box>
            </>
          )}
          </>
          )}

          {/* Uploaded Details Tab Content */}
          {currentTab === 1 && (
            <>
              {/* Statistics Cards - Simple Template UI Colors */}
              {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {/* Total Students */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'primary.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PeopleIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.totalStudents || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Total Students
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Invitations Sent */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'success.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SendIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.invitationSentCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Invitations Sent
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Invitations Pending */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'warning.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PendingIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.invitationPendingCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Invitations Pending
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Portal Connected */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'info.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <LinkIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalConnectedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Portal Connected
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Pending */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'warning.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <AssignmentIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestPendingCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Pending
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Approved */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'success.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircleOutlineIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestApprovedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Approved
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Rejected */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'error.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CancelOutlinedIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestRejectedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Rejected
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Action Bar */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Uploaded Students ({uploadedStudents.length})
                  {uploadedStudents.length > 0 && (
                    <MDTypography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Check console for data)
                    </MDTypography>
                  )}
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    fetchUploadedStudents();
                    fetchStats();
                  }}
                  disabled={loadingStudents}
                >
                  Refresh
                </MDButton>
              </Box>

              {/* Students Grid */}
              {loadingStudents ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : uploadedStudents.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <MDTypography variant="h6" color="text.secondary">
                      No uploaded students found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Upload students using the Upload tab to see them here.
                    </MDTypography>
                  </CardContent>
                </Card>
              ) : (
                <Paper sx={{ height: 'calc(100vh - 400px)', width: '100%', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: '100%',
                      '& .ag-theme-alpine': {
                        fontSize: '12px',
                        height: '100%',
                        width: '100%',
                        '& .ag-header-cell-text': {
                          fontSize: '12px',
                          fontWeight: 600,
                        },
                        '& .ag-cell': {
                          fontSize: '12px',
                          padding: '4px 8px',
                        },
                        '& .ag-row': {
                          minHeight: '40px !important',
                          height: '40px !important',
                        },
                        '& .ag-header-row': {
                          minHeight: '36px !important',
                          height: '36px !important',
                        },
                      },
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      rowData={uploadedStudents}
                      columnDefs={columnDefs}
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                        filter: true,
                      }}
                      pagination={true}
                      paginationPageSize={20}
                      animateRows={true}
                      rowHeight={40}
                      headerHeight={36}
                      className="ag-theme-alpine"
                      domLayout="normal"
                      suppressSizeToFit={true}
                      onGridReady={(params) => {
                        console.log('Grid ready with data:', uploadedStudents.length, 'rows');
                        console.log('Grid row data sample:', uploadedStudents[0]);
                        if (uploadedStudents.length > 0) {
                          console.log('First row keys:', Object.keys(uploadedStudents[0]));
                        }
                      }}
                      onFirstDataRendered={(params) => {
                        console.log('First data rendered');
                        const displayedRows = params.api.getDisplayedRowCount();
                        console.log('Displayed rows:', displayedRows);
                      }}
                    />
                  </Box>
                </Paper>
              )}

              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}
            </>
          )}
        </MDBox>
      </DialogContent>

      {/* Confirmation Dialog */}
      <MuiDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Upload</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text.secondary">
            Do you want to upload these current students? Detected rows: {rows.length}. Please ensure the columns match your template.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>
            No
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleImport}
            autoFocus
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading…' : 'Yes, Upload'}
          </MDButton>
        </DialogActions>
      </MuiDialog>
    </Dialog>
  );
};

AdminUploadCurrentStudents.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminUploadCurrentStudents;

